import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSponsorshipApi } from '../../../Hooks/useSponsorshipApi';
import SponsorForm from '../../../Components/admin/forms/SponsorForm';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Badge } from '../../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '../../../ui/card';
import { Search, Filter } from 'lucide-react';

/**
 * Admin Sponsors Page
 * Lists all sponsors with filtering and CRUD operations
 */
const AdminSponsors = () => {
  const navigate = useNavigate();
  const { sponsors: sponsorsApi } = useSponsorshipApi();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: '', category: '', active: undefined });
  const [showForm, setShowForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);

  // Load sponsors data
  useEffect(() => {
    const loadSponsors = async () => {
      setLoading(true);
      try {
        const response = await sponsorsApi.list({ ...filters, page: pagination.page });
        // Handle both response formats: direct array or payload.sponsors
        const sponsorsData = response?.payload?.sponsors || response?.sponsors || [];
        setSponsors(sponsorsData);
        setPagination({
          total: response?.payload?.count || response?.total || sponsorsData.length,
          page: response?.page || pagination.page,
          totalPages: response?.totalPages || Math.ceil((response?.payload?.count || sponsorsData.length) / 10),
        });
      } catch (error) {
        console.error('Failed to load sponsors:', error);
        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    loadSponsors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handleCreateSponsor = () => {
    setEditingSponsor(null);
    setShowForm(true);
  };

  const handleEditSponsor = (sponsor) => {
    setEditingSponsor(sponsor);
    setShowForm(true);
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (window.confirm('Are you sure you want to delete this sponsor? This action cannot be undone.')) {
      try {
        await sponsorsApi.delete(sponsorId);
        loadSponsors();
      } catch (error) {
        console.error('Failed to delete sponsor:', error);
        alert('Failed to delete sponsor: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const loadSponsors = async () => {
    setLoading(true);
    try {
      const response = await sponsorsApi.list({ ...filters, page: pagination.page });
      const sponsorsData = response?.payload?.sponsors || response?.sponsors || [];
      setSponsors(sponsorsData);
      setPagination({
        total: response?.payload?.count || response?.total || sponsorsData.length,
        page: response?.page || pagination.page,
        totalPages: response?.totalPages || Math.ceil((response?.payload?.count || sponsorsData.length) / 10),
      });
    } catch (error) {
      console.error('Failed to load sponsors:', error);
      setSponsors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    loadSponsors();
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSponsor(null);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sponsors</h1>
          <p className="text-muted-foreground mt-1">Manage sponsorship partners and their details</p>
        </div>
        <Button onClick={handleCreateSponsor}>
          Create Sponsor
        </Button>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search sponsors..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={filters.category || ''}
                onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                <option value="technology">Technology</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={filters.active !== undefined ? filters.active.toString() : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFilterChange({ 
                    ...filters, 
                    active: value === '' ? undefined : value === 'true' 
                  });
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sponsors Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Sponsors ({pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading sponsors...</div>
            </div>
          ) : sponsors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">No sponsors found</p>
              <Button onClick={handleCreateSponsor}>
                Create First Sponsor
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sponsors.map((sponsor) => (
                      <TableRow key={sponsor.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{sponsor.name}</div>
                            {sponsor.description && (
                              <div className="text-sm text-muted-foreground line-clamp-1">
                                {sponsor.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{sponsor.industry_category_id || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant={sponsor.is_active ? 'success' : 'destructive'}>
                            {sponsor.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(sponsor.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditSponsor(sponsor)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSponsor(sponsor.id || sponsor._id)}
                              className="text-destructive hover:text-destructive"
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {sponsors.length} of {pagination.total} sponsors
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Sponsor Form Modal */}
      {showForm && (
        <SponsorForm
          sponsor={editingSponsor}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default AdminSponsors;
